<template>
    <article>
        <main class="page__content page-with-banner">
            <KHero
                class="hero component component--full-width"
                :padding="hasBannerAside ? 'md' : 'lg'"
            >
                <template #media>
                    <NuxtImg
                        v-if="article.content.banner_image?.filename"
                        width="1920"
                        height="900"
                        fit="cover"
                        format="auto"
                        sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw"
                        :src="article.content.banner_image.filename"
                        :alt="article.content.banner_image.alt"
                        :loading="priority ? 'eager' : 'lazy'"
                        :fetchpriority="priority ? 'high' : undefined"
                        :preload="priority ? { fetchPriority: 'high' } : false"
                    />
                </template>

                <template #content>
                    <div
                        class="page-with-banner__content"
                        v-html="renderRichText(article.content.banner_text)"
                    />

                    <div
                        v-if="article.content.banner_buttons?.length"
                        class="page-with-banner__buttons"
                    >
                        <StoryblokComponent
                            v-for="blok in article.content.banner_buttons"
                            :key="blok._uid"
                            :story="article"
                            :blok="blok"
                        />
                    </div>

                    <StoryblokComponent
                        v-for="(blok, index) in article.content.banner_content"
                        :key="blok._uid"
                        :story="article"
                        :blok="blok"
                        :priority="index === 0"
                    />
                </template>
            </KHero>

            <AppBreadcrumbs
                :base-url="runtimeConfig.public.BASE_URL"
                :parents="[
                    {
                        name: config.brand_name,
                        full_slug: '',
                    },
                    ...parentPages,
                ]"
                :current="{
                    name: article.name,
                    full_slug: article.full_slug,
                }"
            />

            <StoryblokComponent
                v-for="(blok, index) in article.content.body"
                :key="blok._uid"
                :story="article"
                :blok="blok"
                :priority="index === 0"
            />
        </main>
    </article>
</template>

<script setup>
import { KHero } from '@vemedia/karibu'
import seoTitle from '~/helpers/seoTitle'

const { data } = defineProps({
    data: {
        type: Object,
        required: true,
    },
})

const config = useState('config')

const route = useRoute()
const { slug } = route.params

const runtimeConfig = useRuntimeConfig()

const { article } = data

// The hero pads tighter once anything sits under the banner text — buttons,
// or the looser banner_content bloks — so it doesn't crowd the top of the image.
const hasBannerAside = computed(() =>
    Boolean(article.content.banner_content?.length || article.content.banner_buttons?.length),
)

const parentPages = computed(() => {
    if (Array.isArray(slug) && slug.length > 1) {
        return slug.filter(item => item !== '').slice(0, -1).map((item, index) => {
            return {
                name: (item.charAt(0).toUpperCase() + item.slice(1)).replace('-', ' '),
                full_slug: `${slug.slice(0, index + 1).join('/')}`,
            }
        })
    }

    return []
})

// Only ever opt OUT: useRobotsRule writes the rule unconditionally, so calling it with a
// truthy value would re-enable indexing on hosts the site-config plugin marks non-indexable.
// `noindex, follow` and not `useRobotsRule(false)`: `false` resolves to the module's
// `robotsDisabledValue` (`noindex, nofollow`), whose nofollow also cuts the crawl path to
// any indexable children.
if (article.content.index === false) {
    useRobotsRule('noindex, follow')
}

useHead({
    title: seoTitle(article),
    meta: [
        {
            hid: 'description',
            name: 'description',
            content: article.content.meta_description,
        },
    ],
})
</script>

<style lang="scss">
@use '@vemedia/bcn-components/scss/responsive' as *;

.page-with-banner {
    &__content {
        text-align: start;

        @include tablet {
            text-align: center;
        }

        h1 {
            font-size: var(--font-size-9);
            line-height: var(--line-height-9);
            letter-spacing: var(--letter-spacing-9);

            &:not(:last-child) {
                margin-bottom: var(--space-4);
            }
        }

        p {
            font-size: var(--font-size-5);
            line-height: var(--line-height-5);
            letter-spacing: var(--letter-spacing-5);
            text-wrap: balance;
        }
    }

    &__buttons {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-3);
        justify-content: start;

        @include tablet {
            justify-content: center;
        }
    }
}
</style>
